import React, { useCallback, useEffect } from 'react'
import { useHistory, useParams } from 'react-router-dom'
import { useForm } from 'react-hook-form'
import {
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
  DoneOutlined as DoneOutlinedIcon
} from '@mui/icons-material'
import jwt from 'jsonwebtoken'

import {
  Button,
  Form,
  Input,
  Span,
  PasswordRequerimentsChecklist
} from 'components'
import { useAuth, useModal } from 'hooks'
import useLoading from 'hooks/useLoading'
import { Header } from 'pages/Login/components/Header/Header'
import GenericServices from 'services/Generic/Generic'
import { UserServices } from 'services/Supplier/Users'
import { COLORS, SPACING, TYPOGRAPHY, VALIDATIONS } from 'utils'
import { passwordValidator } from 'utils/passwordValidator.utils'
import { useToggle } from 'hooks'
import { yup, yupResolver } from 'utils/yup'

import {
  Card,
  Container,
  HeaderCard,
  CardContent
} from './PasswordRecovery.styles'

interface IParam {
  token: string
}

const schema = yup
  .object({
    newPassword: yup.string().required('Informe uma senha').password(),
    confirmPassword: yup
      .string()
      .required('Confirme a senha')
      .min(8, 'Mínimo 8 caracteres.')
      .oneOf([yup.ref('newPassword'), null], 'Senhas se diferem')
  })
  .required()

interface IForm {
  email: string
  newPassword: string
  confirmPassword: string
}

const MAX_PASSWORD_LENGTH = 8

const PasswordRecovery: React.FC = () => {
  const { isPasswordModelValid } = passwordValidator()

  const { push } = useHistory()
  const { showModal } = useModal()
  const { token } = useParams<IParam>()
  const { setLoading } = useLoading()
  const {
    control,
    handleSubmit,
    watch,
    formState: { errors },
    setError,
    clearErrors
  } = useForm<IForm>({ resolver: yupResolver(schema) })

  const { setUser, setAllData, setUsuarioCsn, setUsuarioFornecedor, logout } =
    useAuth()

  const decodedToken = jwt.decode(token)

  const watchNovaSenha = watch('newPassword')
  const watchNovaSenhaConfirm = watch('confirmPassword')

  const [showPassword, toggleShowPassword] = useToggle()

  useEffect(() => {
    if (Date.now() >= decodedToken.exp * 1000) {
      showModal({
        handleConfirm: () => {
          logout()
          push('/')
        },
        hideButtonCancel: true,
        message: 'Token expirado! solicite novamente a recuperação de senha.',
        txtButtonConfirm: 'Fechar',
        type: 'error'
      })
    }
  }, [])

  const validateToken = useCallback(async () => {
    setLoading('loading')
    try {
      const response = await GenericServices.VerifyToken({ token })
      if (!response.data.sucesso) throw new Error(response.data?.mensagem)

      setLoading('idle')
    } catch (error: any) {
      setLoading('idle')
      showModal({
        handleConfirm: () => push('/'),
        hideButtonCancel: true,
        message: error.mensagem || 'Token Invalido.',
        txtButtonConfirm: 'Fechar',
        type: 'error'
      })
    }
  }, [token])

  useEffect(() => {
    setUser(null)
    setAllData(null)
    setUsuarioCsn(null)
    setUsuarioFornecedor(null)
    validateToken()
  }, [validateToken])

  const handleChangePassword = async (data: IForm) => {
    clearErrors()

    if (
      !isPasswordModelValid(watchNovaSenha, 'newPassword', setError) ||
      !isPasswordModelValid(watchNovaSenhaConfirm, 'confirmPassword', setError)
    ) {
      return
    }

    setLoading('loading')

    try {
      const response = await UserServices().changePassword({
        email: decodedToken.email,
        newPassword: data.newPassword,
        token: token
      })

      if (!response.data.sucesso) {
        throw new Error(response.data.mensagem)
      }

      showModal({
        handleConfirm: () => push('/'),
        hideButtonCancel: true,
        message: 'Senha alterada com sucesso.',
        txtButtonConfirm: 'Fechar',
        type: 'success'
      })
      setLoading('idle')
    } catch (error: any) {
      showModal({
        hideButtonCancel: true,
        message:
          error.response.data.mensagem ||
          error.mensagem ||
          'Não foi possivel alterar a senha. Tente novamente.',
        txtButtonConfirm: 'Fechar',
        type: 'error'
      })
      setLoading('idle')
    }
  }

  const getPasswordRequerimentSatisfiedColor = (satisfied: boolean) => {
    if (watchNovaSenha?.length === 0) {
      return 'black'
    }

    if (!satisfied) {
      return COLORS.error
    }

    return COLORS.success
  }

  const passwordMatchIcon = () => {
    if (
      watchNovaSenha !== watchNovaSenhaConfirm ||
      !watchNovaSenha ||
      !watchNovaSenhaConfirm
    ) {
      return null
    }

    if (
      watchNovaSenhaConfirm?.length < MAX_PASSWORD_LENGTH - 1 ||
      watchNovaSenha?.length < MAX_PASSWORD_LENGTH - 1
    ) {
      return null
    }

    return <DoneOutlinedIcon cursor="default" color="success" />
  }

  const disabledSaved = (novaSenha: string, novaSenhaConfirm: string) => {
    if (
      !VALIDATIONS.minCharacters(novaSenha, 8) &&
      !VALIDATIONS.minCharacters(novaSenhaConfirm, 8)
    ) {
      return true
    }
    return false
  }

  return (
    <>
      <Header />
      <Container>
        <Card>
          <HeaderCard>
            <Span
              text="Recuperar Senha"
              color={COLORS.black}
              fontSize="20px"
              fontWeight="700"
            />
          </HeaderCard>
          <CardContent>
            <Form onSubmit={handleSubmit(handleChangePassword)}>
              <Input
                control={control}
                errors={errors}
                formValue="newPassword"
                label="Nova Senha"
                type={showPassword ? 'none' : 'password'}
                onClickIcon={toggleShowPassword}
                inputIcon={
                  showPassword ? (
                    <VisibilityOffIcon cursor="pointer" />
                  ) : (
                    <VisibilityIcon cursor="pointer" />
                  )
                }
                placeholder="Nova Senha"
                marginTop={SPACING.spacing20}
                required
              />
              <Input
                control={control}
                errors={errors}
                formValue="confirmPassword"
                label="Confirmar Senha"
                type="password"
                inputIcon={passwordMatchIcon()}
                placeholder="Confirmar Senha"
                marginTop={SPACING.spacing20}
                required
              />

              <PasswordRequerimentsChecklist password={watchNovaSenha}>
                {(requeriments) =>
                  requeriments.map((requeriment) => (
                    <Span
                      key={requeriment.id}
                      text={requeriment.description}
                      fontSize={TYPOGRAPHY.captionSize12}
                      color={
                        watchNovaSenha
                          ? getPasswordRequerimentSatisfiedColor(
                              requeriment.satisfied
                            )
                          : 'black'
                      }
                    />
                  ))
                }
              </PasswordRequerimentsChecklist>

              <Button
                text="Enviar"
                type="submit"
                marginTop={SPACING.spacing20}
                disabled={disabledSaved(watchNovaSenha, watchNovaSenhaConfirm)}
              />
            </Form>
          </CardContent>
        </Card>
      </Container>
    </>
  )
}

export default PasswordRecovery
